import {
    useState,
    useEffect,
    useCallback,
    useContext,
    createContext,
    ReactNode,
} from 'react';
import {
    Auth0Provider,
    Auth0Context,
    Auth0ContextInterface,
    initialContext,
} from '@auth0/auth0-react';

import Config from 'Config';
import * as logger from 'common/utils/Logger';
import { clearAuthenticationCache } from 'common/services/LogoutService';

interface Auth0Config {
    domain: string;
    clientId: string;
    audience: string;
    isLocal?: boolean;
}

interface Auth0ProviderWithConfigProps {
    children: ReactNode;
}

const onRedirectCallback = () => {
    const cleanUrl = `${globalThis.location.origin}${globalThis.location.hash}`;
    globalThis.history.replaceState({}, document.title, cleanUrl);
};

const fallbackConfig: Auth0Config = {
    domain: Config.auth0Domain,
    clientId: Config.auth0ClientId,
    audience: Config.auth0Audience,
};

const localLogout = () => {
    clearAuthenticationCache();
    globalThis.location.hash = '#/login';
    return Promise.resolve();
};

const localAuth0ContextValue: Auth0ContextInterface = {
    ...initialContext,
    isAuthenticated: true,
    isLoading: false,
    logout: localLogout,
    loginWithRedirect: () => Promise.resolve(),
    getAccessTokenWithPopup: async () => '',
    getIdTokenClaims: async () => undefined,
};

const Auth0ProviderWithConfig = ({
    children,
}: Auth0ProviderWithConfigProps) => {
    const [auth0Config, setAuth0Config] = useState<Auth0Config | null>(null);

    const fetchAuth0Config = useCallback(async () => {
        try {
            const response = await fetch(
                `${Config.apiBaseUrl}/Utilities/auth0Config`
            );
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const config: Auth0Config = await response.json();
            setAuth0Config(config);
        } catch (error) {
            logger.warn(
                'Failed to fetch Auth0 config from S2, using fallback',
                { error }
            );
            setAuth0Config(fallbackConfig);
        }
    }, []);

    useEffect(() => {
        fetchAuth0Config();
    }, [fetchAuth0Config]);

    if (!auth0Config) {
        return null;
    }

    if (auth0Config.isLocal) {
        return (
            <Auth0ConfigContext.Provider value={auth0Config}>
                <Auth0Context.Provider value={localAuth0ContextValue}>
                    {children}
                </Auth0Context.Provider>
            </Auth0ConfigContext.Provider>
        );
    }

    return (
        <Auth0ConfigContext.Provider value={auth0Config}>
            <Auth0Provider
                domain={auth0Config.domain}
                clientId={auth0Config.clientId}
                authorizationParams={{
                    redirect_uri: `${globalThis.location.origin}/#/redirect`,
                    audience: auth0Config.audience,
                    scope: 'profile email openid offline_access',
                }}
                cacheLocation='localstorage'
                useRefreshTokens
                onRedirectCallback={onRedirectCallback}
            >
                {children}
            </Auth0Provider>
        </Auth0ConfigContext.Provider>
    );
};

export const Auth0ConfigContext = createContext<Auth0Config | null>(null);

export const useAuth0Config = (): Auth0Config => {
    const config = useContext(Auth0ConfigContext);
    return config ?? fallbackConfig;
};

export default Auth0ProviderWithConfig;
